/**
 * propertySlice — property reference + entry-variant state.
 *
 * Stores a lightweight `PropertyRef` (BE property-search heavy lifting stays
 * in React Query). Also owns the frozen A/B entry-variant for the session.
 *
 * Admin extension (Option X, Module 4): targetUserId + targetWorkspaceId store
 * the admin-selected client before the main entry flow begins. Client roles
 * never write these fields.
 */

import type { StateCreator } from 'zustand'

import type { EntryVariant, PropertyRef } from 'models/purchaseFlowRevamp'
import type { PfStoreState } from '../store'

export interface PropertySliceState {
  property: PropertyRef | null
  entryVariant: EntryVariant | null
  /** Admin-only: ID of the client user for whom the order is being placed. */
  targetUserId: string | null
  /** Admin-only: workspace ID selected for the order. */
  targetWorkspaceId: string | null
  /** Admin-only: the client's internal user id (UUID), the broker of a property the admin creates for them. */
  targetUserInternalId: string | null
  /**
   * Admin-only: email of the client the admin acts for. Required by the PF v2
   * create-session body (`CreateOrderSessionAdminV2DTO.email`). `null` for
   * client role. Set by the property drawer's client-selection stage
   * (`entry/ClientSelectionSection`) alongside `targetUserId`.
   */
  targetUserEmail: string | null
  /**
   * Admin-only: the target client's own name and phone, carried so the
   * instructions step can prefill the meeting contact with the CLIENT's details
   * rather than only their email.
   *
   * Both come from the client lookup the admin gate already performs. That
   * lookup is a mutation, so its result dies with `ClientSelectionSection` —
   * without capturing them here the instructions step would have to re-fetch a
   * user it has already seen.
   */
  targetUserName: string | null
  targetUserPhone: string | null
  /**
   * Admin-only: whether the SELECTED target workspace carries a paid
   * entitlement. Resolved by the admin gate, because the subscription-gated
   * features must be judged on the client's workspace — the admin's own
   * `hasWorkspaceActiveSubscription` describes the wrong workspace entirely.
   * `null` for a client ordering for themselves, who is judged live instead.
   */
  targetWorkspaceIsSubscribed: boolean | null
  /**
   * Admin-only: may the target CLIENT pay by invoice? Legacy read
   * `targetUser.invoiceAllowed`; the admin's own `userCanPayByInvoice` describes
   * the wrong party, so the admin gate captures the client's. `null` for a
   * client ordering for themselves, who is judged live.
   */
  targetInvoiceAllowed: boolean | null
  /**
   * Admin-only: does the target CLIENT's organization require the
   * representatives' power of attorney as well as the owners'? Legacy read
   * `targetUser.requiresRepresentativePowerOfAttorney`; the admin's own
   * organization describes the wrong party. `null` for a client ordering for
   * themselves, who is judged live.
   */
  targetRequiresPoaRepresentatives: boolean | null
  /** Admin-only: place the order without starting the CT automation (legacy "Skip CT automation"). */
  adminSkipCtAutomation: boolean
  /** Admin-only: place the order without sending it to external providers (legacy "Skip external provider sending"). */
  adminSkipExternalProviderSending: boolean
}

/** What the admin gate resolved about the client it selected. */
export interface AdminTarget {
  email?: string | null
  /** The client's internal user id (UUID). */
  internalId?: string | null
  name?: string | null
  phone?: string | null
  /** Paid entitlement of the SELECTED workspace. */
  isSubscribed?: boolean | null
  /** May the client pay by invoice? (legacy `targetUser.invoiceAllowed`.) */
  invoiceAllowed?: boolean | null
  /** Does the client's organization require the representatives' PoA? */
  requiresPoaRepresentatives?: boolean | null
}

export interface PropertySliceActions {
  setProperty(property: PropertyRef): void
  setEntryVariant(variant: EntryVariant | null): void
  /** Admin gate: persist the selected client + workspace + contact before entering entry flow. */
  setAdminTarget(targetUserId: string, targetWorkspaceId: string, target?: AdminTarget): void
  /** Admin gate: the automation an admin chose to skip. Kept apart from the target, which revalidation rewrites. */
  setAdminSkips(skips: { ctAutomation: boolean, externalProviderSending: boolean }): void
  clear(): void
}

export type PropertySlice = PropertySliceState & PropertySliceActions

export const createPropertySlice: StateCreator<PfStoreState, [], [], PropertySlice> = (set) => ({
  property: null,
  entryVariant: null,
  targetUserId: null,
  targetWorkspaceId: null,
  targetUserInternalId: null,
  targetUserEmail: null,
  targetUserName: null,
  targetUserPhone: null,
  targetWorkspaceIsSubscribed: null,
  targetInvoiceAllowed: null,
  targetRequiresPoaRepresentatives: null,
  adminSkipCtAutomation: false,
  adminSkipExternalProviderSending: false,

  // The order is placed on the session's property, so a different property needs its own session.
  setProperty: (property) => set((state) => ({
    property,
    draft: state.property?.id === property.id
      ? { ...state.draft, dirty: true }
      : {
        ...state.draft,
        dirty: true,
        sessionId: null,
        sessionWorkspaceId: null,
        sessionPropertyId: null,
        sessionStatus: 'idle',
        sessionRecoveryAttempted: false,
        sessionPropertyIncomplete: false,
      },
  })),

  setEntryVariant: (entryVariant) => set({ entryVariant }),

  setAdminTarget: (targetUserId, targetWorkspaceId, target = {}) =>
    set({
      targetUserId,
      targetWorkspaceId,
      targetUserInternalId: target.internalId ?? null,
      targetUserEmail: target.email ?? null,
      targetUserName: target.name ?? null,
      targetUserPhone: target.phone ?? null,
      targetWorkspaceIsSubscribed: target.isSubscribed ?? null,
      targetInvoiceAllowed: target.invoiceAllowed ?? null,
      targetRequiresPoaRepresentatives: target.requiresPoaRepresentatives ?? null,
    }),

  setAdminSkips: ({ ctAutomation, externalProviderSending }) =>
    set((state) => (
      state.adminSkipCtAutomation === ctAutomation && state.adminSkipExternalProviderSending === externalProviderSending
        ? state
        : { adminSkipCtAutomation: ctAutomation, adminSkipExternalProviderSending: externalProviderSending, draft: { ...state.draft, dirty: true } }
    )),

  clear: () => set((state) => ({
    property: null,
    targetUserId: null,
    targetWorkspaceId: null,
    targetUserInternalId: null,
    targetUserEmail: null,
    targetUserName: null,
    targetUserPhone: null,
    targetWorkspaceIsSubscribed: null,
    targetInvoiceAllowed: null,
    targetRequiresPoaRepresentatives: null,
    adminSkipCtAutomation: false,
    adminSkipExternalProviderSending: false,
    draft: { ...state.draft, dirty: true },
  })),
})
